import React, { useEffect, useState } from "react";
import {
  Grid,
  FormControlLabel,
  Checkbox,
  Button,
  makeStyles,
} from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { startOfDay } from "date-fns";
import ToastMessage from "../../../../Common/ToastMessage";
import Textarea from "../../../../Common/Textarea";
import { create, update } from "../../../../../services/followUps.service";
import { getInputDateFormats } from "../../../../../utils/get-date-time-locale";

import { Container, ModalTitle, ButtonsContainer } from "./styles";
import { authProvider } from "../../../../../authProvider";
import { useAuth } from "../../../../../context/auth";
import { checkAccess } from "../../../utils/index";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1vh 1vw",
  },
  subtitle: {
    ...theme.typography.subtitle1,
  },
  button: {
    fontSize: "1rem",
    fontWeight: "bold",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  buttonOutlined: {
    border: "2px solid",
    fontSize: "1rem",
    fontWeight: "bold",
    color: theme.palette.grey.main,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  spacer: {
    marginBottom: 12,
  },
  title: {
    fontWeight: "bold",
    fontSize: "1.25rem",
    color: theme.palette.primary.main,
  },
}));

const FollowUpModal = ({
  action,
  actionGroupId,
  fupToEdit,
  t,
  setOpenFupModal,
  onCreateFupSucces,
}) => {
  const classes = useStyles();
  const { userAccess } = useAuth();
  const history = useHistory();
  const { i18n } = useTranslation();

  const [formValues, setFormValues] = useState({
    comment: "",
    fupDate: new Date(),
    reprogram: false,
    rescheduleDate: new Date(),
    conclusionDate: new Date(),
  });
  const [toastMessage, setToastMessage] = useState({
    open: false,
    content: "",
    type: "",
    autoHideDuration: 4000,
  });
  const [canCreateActionFup, setCanCreateActionFup] = useState();
  const [canRescheduleAction, setCanRescheduleAction] = useState();

  useEffect(() => {
    if (userAccess) {
      const hasAccessToCreateActionFup = checkAccess(
        userAccess,
        "createActionFup"
      );
      const hasAccessToRescheduleAction = checkAccess(
        userAccess,
        "rescheduleAction"
      );

      setCanCreateActionFup(hasAccessToCreateActionFup);
      setCanRescheduleAction(hasAccessToRescheduleAction);

      if (!hasAccessToCreateActionFup && !hasAccessToRescheduleAction) {
        setOpenFupModal(false);
      }
      if (fupToEdit) {
        setFormValues(fupToEdit);
      }
    }
  }, [fupToEdit, userAccess, history]);

  const handleSaveFup = async () => {
    if (!fupToEdit) {
      const createFupRes = await create({
        requesterId: authProvider.account.accountIdentifier,
        commentes: formValues.comment,
        remarked: formValues.reprogram,
        ...(formValues.reprogram && { newDeadLine: formValues.rescheduleDate }),
        idAction: action,
        groupMinutes: actionGroupId,
      });

      if (createFupRes?.error?.length > 0) {
        setToastMessage({
          open: true,
          content: "Ocorreu um erro, tente novamente mais tarde.",
          autoHideDuration: 4000,
          type: "error",
        });

        return;
      }

      onCreateFupSucces();
    } else {
      const updatedFupRes = await update({
        fupId: fupToEdit.id,
        commentes: formValues.comment,
        remarked: formValues.reprogram,
        ...(formValues.reprogram && { newDeadLine: formValues.rescheduleDate }),
        idAction: action,
        groupMinutes: actionGroupId,
      });

      if (updatedFupRes?.error?.length > 0) {
        setToastMessage({
          open: true,
          content: "Ocorreu um erro, tente novamente mais tarde.",
          autoHideDuration: 4000,
          type: "error",
        });

        return;
      }

      onCreateFupSucces();
    }
  };

  return (
    <Container>
      <ToastMessage
        message={toastMessage.content}
        type={toastMessage.type || "error"}
        open={toastMessage.open}
        close={() => setToastMessage({ ...toastMessage, open: false })}
      />
      <span
        style={{ float: "right", cursor: "pointer" }}
        onClick={() => {
          setOpenFupModal(false);
        }}
      >
        [X] {t("meeting.Close")}
      </span>
      <ModalTitle>{t("menu.actions.followUps")}</ModalTitle>

      <Grid container direction="row" spacing={3}>
        <Grid item lg={12} xs={12}>
          <Textarea
            name="commentFup"
            id="commentFup"
            onChange={(e) =>
              setFormValues({ ...formValues, comment: e.target.value })
            }
            value={formValues.comment}
            label={t("menu.actions.commentFollowUp")}
            style={{ width: "100%" }}
          />
        </Grid>

        <Grid item lg={12} xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                value={formValues.reprogram}
                checked={formValues.reprogram}
                onChange={() =>
                  setFormValues({
                    ...formValues,
                    reprogram: !formValues.reprogram,
                  })
                }
                name="reprogram"
                id="reprogram"
                color="primary"
                disabled={!canRescheduleAction}
              />
            }
            label={t("menu.actions.actionReprogram")}
          />
        </Grid>

        {formValues.reprogram && (
          <>
            <Grid item lg={12} xs={12}>
              <KeyboardDatePicker
                autoOk
                disableToolbar
                variant="inline"
                format={getInputDateFormats(i18n.language)}
                id="date-picker-inline"
                fullWidth
                label={t("menu.actions.rescheduleDate")}
                value={formValues.rescheduleDate}
                onChange={(date) => {
                  setFormValues({
                    ...formValues,
                    rescheduleDate: startOfDay(date),
                  });
                }}
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
      <ButtonsContainer>
        <Button
          onClick={() => {
            setOpenFupModal(false);
          }}
          variant="outlined"
          className={classes.buttonOutlined}
        >
          {t("forms.committee.buttons.cancel")}
        </Button>
        <Button
          onClick={handleSaveFup}
          variant="outlined"
          className={classes.button}
          disabled={!canCreateActionFup}
        >
          {t("forms.committee.buttons.save")}
        </Button>
      </ButtonsContainer>
    </Container>
  );
};

export default FollowUpModal;
